import React, { useCallback } from 'react'

import {
  EXPORT_MESSAGE_BY_STATUS,
  formatExportFileName,
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import { intComma } from 'humanize-plus'

import { Link, Table, TableColumn, useToast } from '@driftt/dds'
import { CubeQueryStringAdapter, useCubeExport } from '@driftt/dds.reports'
import { tracking } from '@driftt/traffic-control'

import { useTrackDrillDownClickEvent } from 'components/conversation/hooks/useTrackDrilldownClickEvent'
import useTimePeriod from 'hooks/useTimePeriod'
import { CONVO_DETAIL_REPORT_PATH } from 'layouts/ConversationsDetailReport/ConversationsDetailReport.constants'
import { isNilOrEmpty } from 'utils/ramda'

import { TableWrapper } from '../TableWrapper'

import {
  formatConversationTagDrillDownQuery,
  formatConversationTagsExportQuery,
} from './ConversationTagsTable.helpers'
import useConversationTags, {
  type ConversationTagsMetricRow,
} from './useConversationTags.hook'

export const ConversationTagsTable = () => {
  const { tableData, isLoadingTableData } = useConversationTags()
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()
  const { accessFilters } = useCubeReportAccessFiltersContext()
  const triggerToast = useToast()

  const { trackViewEvent } = useTrackDrillDownClickEvent(
    'ConversationTagsTable'
  )

  const formatDrillDownURL = (tagName: string): string => {
    const cubeQuery = formatConversationTagDrillDownQuery(
      tagName,
      timePeriod?.startDate,
      timePeriod?.endDate,
      [...pageFilters, ...accessFilters]
    )

    return `${CONVO_DETAIL_REPORT_PATH}?${CubeQueryStringAdapter.toQueryString(
      cubeQuery
    )}`
  }

  const { isInitialLoading: isDownloadingExport, refetch: exportCubeData } =
    useCubeExport({
      query: formatConversationTagsExportQuery(
        timePeriod.startDate,
        timePeriod.endDate,
        [...pageFilters, ...accessFilters]
      ),
      enabled: false,
      exportFileName: formatExportFileName(
        'Chat tags',
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate()
      ),
      onExportSuccess() {
        triggerToast({
          message: EXPORT_MESSAGE_BY_STATUS.SUCCESS,
          variant: 'success',
        })
      },
      onExportError() {
        triggerToast({
          message: EXPORT_MESSAGE_BY_STATUS.ERROR,
          variant: 'error',
        })
      },
    })

  const onExportButtonClick = useCallback(() => {
    tracking.trackEvent('CUBE_DRILL_DOWN_EXPORT_CLICK', {
      metricType: 'ConversationTagsTableExport',
      startDate: timePeriod.startDate.toDate(),
      endDate: timePeriod.endDate.toDate(),
      pageName: window.location.pathname,
    })
    exportCubeData()
  }, [exportCubeData, timePeriod.startDate, timePeriod.endDate])

  const defaultColumns: TableColumn<ConversationTagsMetricRow>[] = [
    {
      header: 'Tag',
      value: 'tagName',
      cell: ({ tagName }) => tagName ?? '--',
      disableSort: true,
    },
    {
      header: 'Chats',
      value: 'conversationCount',
      cell: ({ tagName, conversationCount }) => (
        <Link
          variant="inline"
          target="__blank"
          href={formatDrillDownURL(tagName)}
          onClick={() =>
            trackViewEvent({ totalRecords: conversationCount ?? 0 })
          }
        >
          {intComma(conversationCount)}
        </Link>
      ),
      disableSort: true,
    },
  ]

  return (
    <TableWrapper
      title={'Chat tags'}
      description={'How are chats being tagged?'}
      actionText={'Export CSV'}
      onClickAction={isNilOrEmpty(tableData) ? undefined : onExportButtonClick}
      isLoadingAction={isDownloadingExport}
      rootProps={{
        id: 'chats-report--conversation-tags-table',
      }}
    >
      <Table
        height={400}
        rows={tableData}
        columns={defaultColumns}
        isLoading={isLoadingTableData}
        loadingRows={10}
        disableBoxShadow={true}
      />
    </TableWrapper>
  )
}
